import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import api from '../../services/api'
import {
  GET_USERS,
  GET_USER,
  POST_ADD_USER,
  PUT_UPDATE_USER,
  DELETE_USER
} from '../../helpers/url_helper'

// Async thunk for fetching all users
export const fetchUsers = createAsyncThunk(
  'user/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(GET_USERS)
      return response.data.users
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to fetch users'
      if (error.response?.status !== 401) toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for fetching single user
export const fetchUser = createAsyncThunk(
  'user/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`${GET_USER}/${id}`)
      return response.data.user
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to fetch user details'
      if (error.response?.status !== 401) toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for adding user
export const addUser = createAsyncThunk(
  'user/add',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.post(POST_ADD_USER, userData)
      toast.success('User added successfully')
      return response.data.user
    } catch (error) {
      const message = error.response?.data?.error || 
                     error.response?.data?.message || 
                     'Failed to add user'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for updating user
export const updateUser = createAsyncThunk(
  'user/update',
  async ({ id, userData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`${PUT_UPDATE_USER}/${id}`, userData)
      toast.success('User updated successfully')
      return response.data.user
    } catch (error) {
      const message = error.response?.data?.error || 
                     error.response?.data?.message || 
                     'Failed to update user'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for deleting user
export const deleteUser = createAsyncThunk(
  'user/delete',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`${DELETE_USER}/${id}`)
      toast.success('User deleted successfully')
      return id
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to delete user'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

const initialState = {
  users: [],
  selectedUser: null,
  loading: false,
  error: null,
}

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null
    },
    clearSelectedUser: state => {
      state.selectedUser = null
    },
  },
  extraReducers: builder => {
    builder
      // Fetch All Users
      .addCase(fetchUsers.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false
        state.users = action.payload
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Fetch Single User
      .addCase(fetchUser.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.loading = false
        state.selectedUser = action.payload
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Add User
      .addCase(addUser.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(addUser.fulfilled, (state, action) => {
        state.loading = false
        state.users.push(action.payload)
      })
      .addCase(addUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Update User
      .addCase(updateUser.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false
        const index = state.users.findIndex(user => user.id === action.payload.id)
        if (index !== -1) {
          state.users[index] = action.payload
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Delete User
      .addCase(deleteUser.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false
        state.users = state.users.filter(user => user.id !== action.payload)
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { clearError, clearSelectedUser } = userSlice.actions
export default userSlice.reducer
